import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzAlgDataService,
  ClientEntity,
  IncomeTaxEntity,
  Rekening,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {BokkzIbDataService} from '@snelstart-ventures/fiscaal/domain-logic';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../gedeelde-componenten/gedeelde-componenten.component';
import {Subject, takeUntil} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiIconModule} from "@snelstart/ui-icon";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-box3-bankrekeningen',
  templateUrl: './box3-bankrekeningen.component.html',
  styleUrls: ['./box3-bankrekeningen.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ]
})
export class Box3BankrekeningenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  private focusedElementId: string | null = null;
  public clientId!: string;
  public dialogVisible = false;
  public index = 0;
  public heeftOWR: boolean[] = [];
  public clientEntity  = {} as ClientEntity;
  public incomeTaxEntity = {} as IncomeTaxEntity;
  public bankrekeningenFg = {} as FormGroup;
  public jaar = 2024;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  constructor(public dataService: BokkzIbDataService,
              public dataAlgService: BokkzAlgDataService,
              private fb: FormBuilder) {
    this.bankrekeningenFg = this.fb.group({
      box3Bankrekeningen: this.fb.array<Rekening>([])
    });
  }

  ngOnInit(): void {
    this.dataService.incomeTaxEntity.pipe(takeUntil(this.destroy$)).subscribe((incomeTaxEntity) => {
      this.incomeTaxEntity = incomeTaxEntity;
      this.jaar = incomeTaxEntity.year!;
      if (incomeTaxEntity.aangifte) {
        this.updateForm()
        this.updateForm();
        this.updateHeeftOWR();
      }
    });
    this.dataAlgService.client.pipe(takeUntil(this.destroy$)).subscribe((client) => {
      this.clientId = client.sstRelationId!;
    });
  }

  ngOnDestroy(): void {
    if (this.destroy$) {
      this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
      this.destroy$.next();
      this.destroy$.unsubscribe(); // Unsubscribe to avoid memory leaks
    }
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  get box3Bankrekeningen(): FormArray {
    return this.bankrekeningenFg.get('box3Bankrekeningen') as FormArray;
  }

  addRij(): void {
    this.box3Bankrekeningen.push(this.createFormGroup()
    );
  }

  verwijderRij(index: number): void {
    this.box3Bankrekeningen.removeAt(index);
    this.bankrekeningenFg.updateValueAndValidity();
    this.bankrekeningenFg.markAsDirty();
    this.saveForm();
  }

  createFormGroup(item?: Rekening): FormGroup {
    return this.fb.group({
      omschrijving: item ? item.omschrijving : undefined,
      bedrag: item ? item.bedrag : undefined,
      rekeningNummer: item ? item.rekeningNummer : undefined,
      landCode: item ? item.landCode : 'NLD',
      groenRekening: item ? item.groenRekening : false,
      waardeEindJaar: item ? item.waardeEindJaar : undefined,
      directRendement: item ? item.directRendement : undefined,
      valutaResultaat: item ? item.valutaResultaat : undefined
    });
  }

  private updateForm(): void {
    const lijst = this.incomeTaxEntity.aangifte?.box3?.bankrekeningen || [];
    const formArray = this.bankrekeningenFg.get('box3Bankrekeningen') as FormArray;
    const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;
    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i]);
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup);
      }
    }

    if (focusedControl && document.contains(focusedControl)) {
      focusedControl.focus();
    }
  }

  saveForm(): void {
    this.incomeTaxEntity.aangifte = {
      ...this.incomeTaxEntity.aangifte,
      box3: {
        ...this.incomeTaxEntity.aangifte?.box3,
        bankrekeningen: this.bankrekeningenFg.value.box3Bankrekeningen
      }
    } as Aangifte;
    this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
    this.updateHeeftOWR();
  }

  getOWRDialogTitel(i: number): string {
    const omschrijving = this.box3Bankrekeningen.controls[i]?.get('omschrijving')?.value;
    const rekeningnummer = this.box3Bankrekeningen.controls[i]?.get('rekeningNummer')?.value;

    if (!omschrijving || !rekeningnummer) {
      return 'Werkelijk rendement bankrekening';
    }
    return `Werkelijk rendement: ${omschrijving} - ${rekeningnummer}`;
  }

  updateHeeftOWR(): void {
    this.heeftOWR = this.box3Bankrekeningen.controls.map((item) => {
      const velden = ['waardeEindJaar', 'directRendement', 'valutaResultaat'];
      return velden.some(veld => item?.get(veld)?.value);
    });
  }

  getBerekendeWerkelijkRendement(i: number): number | null {
    const control = this.box3Bankrekeningen.at(i);
    const directRendement = Number(control?.get('directRendement')?.value) || 0;
    const valutaResultaat = Number(control?.get('valutaResultaat')?.value) || 0;

    // Return null als beide waarden leeg zijn
    if (!control?.get('directRendement')?.value && !control?.get('valutaResultaat')?.value) {
      return null;
    }

    return directRendement + valutaResultaat;
  }

  getGroen(i: number): string {
    const control = this.box3Bankrekeningen.at(i);
    const isGroenRekening = control?.get('groenRekening')?.value || false;

    return isGroenRekening
      ? `(voor toepassen vrijstelling groene bankrekeningen)`
      : '';
  }

}
